/**
 * Labstep
 *
 * @module screens/Folder/Index
 * @desc Index page for Folders of type ExperimentWorkflow or ProtocolCollection
 */

import { EntityEmptyState } from 'labstep-web/components/Entity/EmptyState';
import EntitySearch from 'labstep-web/components/Entity/Search';
import { BulkAction } from 'labstep-web/components/Entity/Search/BulkActions';
import { FolderActionCreateContainer as FolderActionCreate } from 'labstep-web/components/Folder/Action/Create';
import { Action } from 'labstep-web/components/Folder/Action/Menu';
import { FolderTable } from 'labstep-web/components/Folder/Table';
import { Filter } from 'labstep-web/constants/filters';
import { withActiveGroup } from 'labstep-web/containers/ActiveGroup';
import type { WithActiveGroupInjectedProps } from 'labstep-web/containers/ActiveGroup/types';
import { Folder, FolderType } from 'labstep-web/models/folder.model';
import type React from 'react';
import { useMemo } from 'react';

interface FolderIndexProps extends WithActiveGroupInjectedProps {
  entityType?: 'ExperimentWorkflow' | 'ProtocolCollection';
}

export const FolderIndex: React.FC<FolderIndexProps> = ({
  activeGroup,
  entityType = 'ExperimentWorkflow',
}) => {
  const folderType =
    entityType === 'ExperimentWorkflow'
      ? FolderType.experiment_workflow
      : FolderType.protocol_collection;

  const params = useMemo(
    () => ({
      group_id: activeGroup?.id,
      type: folderType,
    }),
    [activeGroup?.id, folderType],
  );

  const action = useMemo(
    () => (
      <FolderActionCreate
        folderType={folderType}
        actionComponentProps={{ type: 'button' }}
      />
    ),
    [folderType],
  );

  const noResultsMessage = useMemo(
    () => (
      <EntityEmptyState
        entityName={Folder.entityName}
        searchParams={params}
      />
    ),
    [params],
  );

  return (
    <EntitySearch
      entityName={Folder.entityName}
      params={params}
      tableFormat
      action={action}
      filters={[Filter.deleted, Filter.created_by]}
      noResultsMessage={noResultsMessage}
      bulkActions={[
        BulkAction.delete,
        BulkAction.assign,
        BulkAction.update_outer,
      ]}
    >
      {({ entities, ...rest }): React.ReactElement => (
        <FolderTable
          folders={entities}
          actions={[
            Action.edit,
            Action.share,
            Action.delete,
            Action.add_subfolders,
            Action.update_outer,
            Action.assign,
          ]}
          {...rest}
        />
      )}
    </EntitySearch>
  );
};

export default withActiveGroup(FolderIndex);
